import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  UniversalEmptyState,
  Card,
  Button,
  NotificationCard,
  SearchField,
  StatusBadge,
} from '@sighthost/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import {
  adminActionQueueService,
  filterByChip,
  type ActionQueueChip,
  type ActionQueueRow,
} from '@/domains/admin/services/adminActionQueueService';

/**
 * ActionQueuePage — Sprint Admin-B B-1.
 *
 * Single scrollable list of every actionable item across 9 sources. Chip-bar
 * filters client-side. Each row carries a deep-link to the relevant detail
 * page.
 *
 * Spec: _bmad-output/SPRINT_ADMIN_B_IMPLEMENTATION_PLAN_2026-05-13.md §2.
 */

const CHIPS: { value: ActionQueueChip; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'billing', label: 'Billing' },
  { value: 'domains', label: 'Domains' },
  { value: 'hosting', label: 'Hosting' },
  { value: 'services', label: 'Services' },
];

export default function ActionQueuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialChip = (searchParams.get('chip') as ActionQueueChip) || 'all';

  const [rows, setRows] = useState<ActionQueueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [chip, setChip] = useState<ActionQueueChip>(initialChip);

  async function load() {
    setRefreshing(true);
    try {
      const data = await adminActionQueueService.list();
      setRows(data);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function onChipChange(next: ActionQueueChip) {
    setChip(next);
    if (next === 'all') {
      searchParams.delete('chip');
    } else {
      searchParams.set('chip', next);
    }
    setSearchParams(searchParams, { replace: true });
  }

  /**
   * SEARCH ONLY — no date control, and that is a ruling, not an oversight.
   *
   * AR-4 / Rule S-2: a filter's blast radius is the container it sits in, and
   * nothing on this page may imply the action queue itself is filtered. The
   * queue is what needs attention NOW; hiding part of it behind a date is how
   * an item ages out unnoticed, which is the exact failure the 2026-09-10
   * attention-loop work was done to stop.
   *
   * The type settles it independently: `ActionQueueRow` carries no timestamp at
   * all — only `aged_minutes` — so there is no event date to filter on even if
   * one were wanted.
   */
  // Chip filtering stays in `filterByChip` — it owns the chip -> source map and
  // is already tested. Re-expressing it as a facet predicate here would be a
  // second copy of that mapping, which is the duplication §4.2 exists to stop.
  const byChip = useMemo(() => (rows ? filterByChip(rows, chip) : undefined), [rows, chip]);

  const table = useTableFilters<ActionQueueRow>({
    rows: byChip,
    searchText: (r) => [r.title, r.detail, r.entity_kind].join(' '),
  });

  const filtered = table.rows;

  const breachCount = useMemo(
    () => (rows ?? []).filter((r) => r.priority === 'breach').length,
    [rows],
  );

  return (
    <UniversalPage
      title="Action queue"
      description="Everything that needs staff attention, ordered by breach-first then age."
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Ops', href: '/admin/ops/audit' },
        { label: 'Action queue' },
      ]}
      actions={
        <Button variant="marketing" size="sm" onClick={load} disabled={refreshing}>
          <RefreshCw className={`mr-1 size-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      }
    >
      {error && <NotificationCard tone="danger" title="Could not load action queue" body={error} />}

      <Card interactive={false} className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          {CHIPS.map((c) => {
            const isSelected = chip === c.value;
            return (
              <Button
                key={c.value}
                size="sm"
                variant={isSelected ? 'primary' : 'outline'}
                onClick={() => onChipChange(c.value)}
              >
                {c.label}
              </Button>
            );
          })}
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search title, detail, kind"
            className="ml-auto w-full max-w-xs"
          />
          {breachCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-danger">
              <AlertTriangle className="size-3.5" aria-hidden />
              {breachCount} breach{breachCount === 1 ? '' : 'es'}
            </span>
          )}
        </div>
        {/* Rule T-4 — this page renders a <ul>, not a UniversalTable, so the
            count lives here rather than in a table caption. */}
        {rows && (
          <p className="mt-2 text-xs text-muted-foreground">{table.caption}</p>
        )}
      </Card>

      {!rows && !error && <UniversalLoadingState variant="rows" count={6} />}

      {rows && filtered.length === 0 && (
        <Card interactive={false} className="mt-4">
          <UniversalEmptyState
            icon={Inbox}
            title="Inbox zero"
            description={
              chip === 'all'
                ? table.q
                  ? 'Nothing matches that search. Clear it to see the whole queue.'
                  : 'Nothing needs immediate attention. Take a breath.'
                : 'No items in this category right now.'
            }
          />
        </Card>
      )}

      {rows && filtered.length > 0 && (
        <ul className="mt-4 space-y-2" aria-label="Action queue items">
          {filtered.map((row) => (
            <ActionRow key={`${row.source}:${row.entity_id}`} row={row} />
          ))}
        </ul>
      )}
    </UniversalPage>
  );
}

function ActionRow({ row }: { row: ActionQueueRow }) {
  const isBreach = row.priority === 'breach';
  return (
    <li>
      <Link
        to={row.href}
        className={`block rounded-sm border px-4 py-3 transition-colors ${
          isBreach
            ? 'border-danger/30 bg-danger/5 hover:bg-danger/10'
            : 'border-border bg-card-subtle hover:bg-card-hover'
        }`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span
                className={`inline-block size-2 shrink-0 rounded-full ${
                  isBreach ? 'bg-danger' : 'bg-warning'
                }`}
                aria-hidden
              />
              <p className="truncate text-sm font-medium text-foreground">{row.title}</p>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{row.detail}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <StatusBadge tone={isBreach ? 'overdue' : 'pending'}>
              {isBreach ? 'Breach' : 'Attention'}
            </StatusBadge>
            <span className="text-xs text-muted-foreground">Open →</span>
          </div>
        </div>
      </Link>
    </li>
  );
}
